import type { JSX } from "react";
import { logData } from "@jetblue/dotcom-fe-logger";
import { AsappClient } from "./client";

type AsappState = Partial<{
  hasContext: boolean;
  hasCustomerId: boolean;
  isFullscreen: boolean;
  isLiveChat: boolean;
  isLoggingIn: boolean;
  isMobile: boolean;
  isOpen: boolean;
  unreadMessages: number;
  isActiveSession: boolean;
}>;

type AsappProperties = {
  asappAppId?: string | undefined;
  asappHostname?: string | undefined;
  affiliateId?: string | undefined;
  sherpaAppId?: string | undefined;
  language?: string | undefined;
  asappAutoOpenOnLoadUrls?: string[] | undefined;
  asappCanBeginChatUrls?: string[] | undefined;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- window must be an interface
  interface Window {
    ASAPP?: {
      (action: "getState", callback: (state: AsappState) => void): void;
      (
        action: "load",
        config: typeof defaultLoadProperties & {
          onLoadComplete?: (isDisplayingChat: boolean) => void;
        },
      ): void;
      (action: string, ...args: unknown[]): void;
    };
  }
  var ASAPP: Window["ASAPP"];
}

const defaultLoadProperties = {
  APIHostname: "jetblue.asapp.com",
  AppId: "jetblue",
  Display: {
    Align: "right",
    AlwaysShowMinimize: true,
    BadgeColor: "#0033a0",
    BadgeType: "badge",
    DarkColor: "#121212",
    PrimaryColor: "#0033a0",
  },
  Language: "en",
};

const ASAPP_SUPPORTED_LANGUAGES = new Set(["en", "fr", "es"]);

const toAsappLanguage = (language: string | undefined) =>
  language && ASAPP_SUPPORTED_LANGUAGES.has(language)
    ? language
    : defaultLoadProperties.Language;

type HandleAsappLoadOptions = {
  asappAppId: string;
  asappHostname: string;
  autoOpenOnLoadUrls: Set<string>;
  canBeginChatUrls: Set<string>;
  language: string | undefined;
  pathname: string;
};

const handleAsappLoad = ({
  asappAppId,
  asappHostname,
  autoOpenOnLoadUrls,
  canBeginChatUrls,
  language,
  pathname,
}: HandleAsappLoadOptions) => {
  if (typeof globalThis.ASAPP === "function") {
    globalThis.ASAPP("getState", (state: AsappState) => {
      const hasActiveSession = Boolean(state.isActiveSession);

      const wasOpen = Boolean(state.isOpen);
      const canBeginNewChat = canBeginChatUrls.has(pathname);
      if (!hasActiveSession && !canBeginNewChat) {
        void logData(
          "ASAPP: No active session and this page cannot begin a new chat, chat will not be loaded",
        );
        return;
      }

      let sanitizedHostname = asappHostname.replace(/^https?:\/\//, "");
      while (sanitizedHostname.endsWith("/")) {
        sanitizedHostname = sanitizedHostname.slice(0, -1);
      }
      const loadProperties = {
        ...defaultLoadProperties,
        APIHostname: sanitizedHostname,
        AppId: asappAppId,
        Language: toAsappLanguage(language),
        onLoadComplete: (isDisplayingChat: boolean) => {
          void logData({
            isDisplayingChat,
            message: "ASAPP: onLoadComplete",
          });
          const shouldForceShow = autoOpenOnLoadUrls.has(pathname);
          if ((!isDisplayingChat && shouldForceShow) || wasOpen) {
            globalThis.ASAPP?.("show");
          }
        },
      };
      void logData({
        loadProperties,
        message: hasActiveSession
          ? "ASAPP: Active session detected, loading chat"
          : "ASAPP: Page allows starting a new chat, loading chat",
      });
      globalThis.ASAPP?.("load", loadProperties);
    });
  }
};

export function Asapp({
  asappAppId,
  asappHostname,
  affiliateId,
  sherpaAppId,
  language,
  asappAutoOpenOnLoadUrls,
  asappCanBeginChatUrls,
}: Readonly<AsappProperties>): JSX.Element | null {
  if (!asappAppId || !asappHostname || !affiliateId || !sherpaAppId) {
    return null;
  }
  return (
    <AsappClient
      affiliateId={affiliateId}
      asappAppId={asappAppId}
      asappAutoOpenOnLoadUrls={asappAutoOpenOnLoadUrls}
      asappCanBeginChatUrls={asappCanBeginChatUrls}
      asappHostname={asappHostname}
      language={language}
      sherpaAppId={sherpaAppId}
    />
  );
}

Asapp.asappLoad = handleAsappLoad;

export default Asapp;
